import { useContext, useCallback, useMemo, useState, useEffect } from 'react'
import styled from '@emotion/styled'
import { t } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'
import Card from '@mui/material/Card'
import FormControl from '@mui/material/FormControl'
import FormControlLabel from '@mui/material/FormControlLabel'
import Radio from '@mui/material/Radio'
import RadioGroup from '@mui/material/RadioGroup'
import Typography from '@mui/material/Typography'
import { arrayOf, bool, number, shape, string } from 'prop-types'
import { FeatureDict, selectFeatureIncludes } from '@authed/selectors'
import { CvsTextField, CvsAutoComplete } from '@components'
import { formatSelectedOptions } from '@components/select/cvs-autocomplete'
import { connectorContext, folderContext } from '@contexts'
import { awsRegions } from '@global/constants'
import { useAppSelector } from '@redux/hooks'
import { templateCopyContext } from '../context'

const FullWidth = styled.div`
  width: 100%;
`
const Flex = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
`

const FlexRadioInline = styled(RadioGroup)`
  margin-top: 30px;
  display: flex;
  flex-direction: row;
`
const AutoComplete = styled(CvsAutoComplete)`
  margin-top: -8px;
  margin-right: 12px;
`

const AutoCompleteMore = styled(CvsAutoComplete)`
  margin-top: -20px;
  margin-bottom: -4px;
`

const MarginText = styled(CvsTextField)`
  margin-top: 12px;
  margin-bottom: 8px;
  margin-right: 12px;
`

const StyledCard = styled(Card)`
  display: inline-flex;
  margin-top: 24px;
`

const Monospace = styled.span`
  font-family: monospace;
  font-weight: bold;
`
const StyledTypography = styled(Typography)`
  margin-bottom: 12px;
`

const Warning = styled.div`
  background-color: #edcf69;
  width: 16px;
`

const Padding = styled.div`
  padding: 16px;
`

const Clickable = styled.span`
  text-decoration: underline;
  :hover {
    color: #25515b;
    cursor: pointer;
  }
`

export const S3in = ({ assignment }) => {
  const { template, onChange } = useContext(templateCopyContext)
  const { byId } = useContext(connectorContext)
  const { byConnectorId, fetchFolderByConnectorId } = useContext(folderContext)
  const [showError, setShowError] = useState(false)
  const [ENABLE_AUTO_SUBMIT = false] = useAppSelector(
    selectFeatureIncludes([FeatureDict.ENABLE_AUTO_SUBMIT]),
  )

  const isAutoSubmitEnabled = useMemo(
    () => ENABLE_AUTO_SUBMIT && !!template?.template?.settings?.enable_auto_submit,
    [ENABLE_AUTO_SUBMIT, template?.template?.settings?.enable_auto_submit],
  )

  const connectorId = useMemo(
    () => assignment?.connector_id ?? assignment?.connectorId,
    [assignment.connector_id, assignment.connectorId],
  )

  const connectors = useMemo(
    () =>
      Object.values(byId)
        ?.filter(
          connector =>
            connector.connector_type_id ===
            (assignment.connector_type_id
              ? assignment.connector_type_id
              : byId[connectorId].connector_type_id),
        )
        ?.map(connector => ({ value: connector.id, label: connector.name })) || [],
    [byId, assignment.connector_type_id, connectorId],
  )

  useEffect(() => {
    if (connectorId) {
      fetchFolderByConnectorId(connectorId).catch(() => {
        setShowError(true)
      })
    }
  }, [fetchFolderByConnectorId, connectorId])

  const folderOptions = useMemo(
    () =>
      (byConnectorId?.[connectorId] || []).map(opt => ({
        value: opt.aws_bucket_name,
        label: opt.aws_bucket_name,
      })),
    [byConnectorId, connectorId],
  )

  const fields = useMemo(
    () =>
      Object.values(template?.fieldsById)
        ?.filter(field => field.name !== 'Claravine ID')
        ?.map(field => ({
          label: field.name,
          value: field.id,
        })) || [],
    [template.fieldsById],
  )

  const handleChange = (key, value) => {
    onChange(prev => ({
      ...prev,
      [assignment.id]: {
        ...prev[assignment.id],
        [key]: value,
      },
    }))
  }

  const handleMetadataChange = (key, value) => {
    onChange(prev => ({
      ...prev,
      [assignment.id]: {
        ...prev[assignment.id],
        metadata: {
          ...prev[assignment.id].metadata,
          [key]: value,
        },
      },
    }))
  }

  const onIdentifierFieldIdsChange = useCallback(
    value => {
      onChange(old => ({
        ...old,
        [assignment.id]: {
          ...old[assignment.id],
          metadata: {
            ...old[assignment.id].metadata,
            identifier_field_ids: value.join(','),
          },
        },
      }))
    },
    [assignment.id, onChange],
  )

  const identifierFieldIds = useMemo(
    () => assignment?.metadata?.identifier_field_ids?.split(',') ?? [],
    [assignment.metadata.identifier_field_ids],
  )

  return (
    <FullWidth>
      <CvsTextField
        fullWidth
        label={t`Name`}
        name="name"
        onChange={e => handleChange('name', e.target.value)}
        value={assignment.name}
      />
      <Flex>
        <AutoComplete
          label={t`AWS account`}
          onSelected={e => handleChange('connector_id', e)}
          options={connectors}
          selected={connectorId}
        />
        <AutoComplete
          label={t`Region`}
          onSelected={value => handleMetadataChange('awsRegion', value)}
          options={awsRegions.CURRENT}
          selected={assignment.metadata.awsRegion}
        />
        <FormControl variant="standard" component="fieldset">
          <FlexRadioInline
            onChange={(e, value) => handleMetadataChange('folder_entry_type', value)}
            value={assignment.metadata.folder_entry_type || 'list'}
          >
            <FormControlLabel value="manual" control={<Radio />} label={t`Manual bucket entry`} />
            <FormControlLabel value="list" control={<Radio />} label={t`List buckets`} />
          </FlexRadioInline>
        </FormControl>
      </Flex>
      {assignment.metadata.folder_entry_type === 'manual' ? (
        <MarginText
          label={t`S3 bucket`}
          onChange={e => handleMetadataChange('folder', e.target.value)}
          value={assignment.metadata.folder}
        />
      ) : !showError && folderOptions ? (
        <AutoCompleteMore
          label={t`S3 bucket`}
          onSelected={value => handleMetadataChange('folder', value)}
          options={folderOptions}
          selected={assignment.metadata.folder}
        />
      ) : (
        <StyledCard>
          <Warning />
          <Padding>
            <StyledTypography>
              <b>{t`Hmm. Looks like we're missing some permissions.`}</b>
            </StyledTypography>
            <StyledTypography>
              <Trans>
                Unfortunately, we were unable to retrieve your AWS S3 Buckets. This can happen when
                the provided AWS key and secret have expired or have not been granted the{' '}
                <Monospace>s3:ListBucket</Monospace> permission.
              </Trans>
            </StyledTypography>
            <StyledTypography>
              <Trans>
                For more information about how to assign the <Monospace>s3:ListBucket</Monospace>{' '}
                permission, please refer to the AWS S3 documentation at
                https://docs.aws.amazon.com/AmazonS3/latest/dev/using-with-s3-actions.html.
              </Trans>
            </StyledTypography>
            <Typography>
              <Trans>
                If you don't want to grant the <Monospace>s3:ListBucket</Monospace> permission, you
                can manually enter the Bucket Name by clicking{' '}
                <Clickable onClick={() => handleChange('folder_entry_type', 'manual')}>
                  here
                </Clickable>
                .
              </Trans>
            </Typography>
          </Padding>
        </StyledCard>
      )}
      <MarginText
        label={t`File prefix`}
        onChange={e => handleMetadataChange('filePrefix', e.target.value)}
        value={assignment.metadata.filePrefix}
      />
      {isAutoSubmitEnabled && (
        <CvsAutoComplete
          options={fields || []}
          label={t`Unique identifier field`}
          selected={formatSelectedOptions(true, identifierFieldIds, fields || [])}
          onSelected={onIdentifierFieldIdsChange}
          isMulti={true}
          required={true}
        />
      )}
    </FullWidth>
  )
}

S3in.propTypes = {
  assignment: shape({
    account_id: number,
    assigned_entity_id: number,
    assigned_entity_type: string,
    config_hash: string,
    connector_id: number,
    created_at: string,
    id: number,
    integration_id: number,
    name: string,
    slug: string,
    updated_at: string,
    included_output_fields: arrayOf(
      shape({
        display: string,
        id: number,
        isHidden: bool,
        isMeta: bool,
        name: string,
        selected: bool,
      }),
    ),
    metadata: shape({
      awsRegion: string,
      bcc: string || undefined,
      cc: string || undefined,
      delimiter: string,
      folder_entry_type: string,
      folder: string,
      to: string || undefined,
    }),
  }).isRequired,
}
